import React, { useEffect } from 'react';
import { Container, Title, Text, List, ThemeIcon, Card, createStyles, Anchor, Group, Stack, Badge } from '@mantine/core';
import { IconTruck, IconMapPin, IconCalendarTime, IconCurrencyRupee, IconEye, IconAlertTriangle } from '@tabler/icons-react';

const useStyles = createStyles((theme) => ({
  wrapper: {
    paddingTop: 80,
    paddingBottom: 80,
    minHeight: '100vh',
    backgroundColor: '#050505',
    backgroundImage: 'radial-gradient(circle at center, #1a1a1a 0%, #000000 100%)',
    color: theme.colors.gray[3],
    [theme.fn.smallerThan('sm')]: {
      paddingTop: 60,
      paddingBottom: 40,
    },
  },

  title: {
    fontSize: 34,
    fontWeight: 800,
    marginBottom: theme.spacing.xl,
    color: theme.white,
    textAlign: 'center',
    [theme.fn.smallerThan('sm')]: {
      fontSize: 28,
      marginBottom: theme.spacing.md,
    },
  },

  sectionTitle: {
    fontSize: 22,
    fontWeight: 700,
    marginTop: theme.spacing.xl * 1.5,
    marginBottom: theme.spacing.md,
    color: theme.colors.cyan[4],
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    [theme.fn.smallerThan('sm')]: {
      fontSize: 18,
      marginTop: theme.spacing.lg,
    },
  },

  content: {
    fontSize: 16,
    lineHeight: 1.6,
    color: theme.colors.gray[4],
    [theme.fn.smallerThan('sm')]: {
      fontSize: 14,
    },
  },

  card: {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.05)',
    padding: theme.spacing.xl,
    borderRadius: theme.radius.lg,
    [theme.fn.smallerThan('sm')]: {
      padding: theme.spacing.md,
    },
  },

  list: {
    marginTop: theme.spacing.sm,
    marginBottom: theme.spacing.sm,
    color: theme.colors.gray[3],
  }
}));

export default function ShippingPolicy() {
  const { classes } = useStyles();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={classes.wrapper}>
      <Container size="md">
        <Card className={classes.card}>
          <Title className={classes.title}>Shipping Details</Title>

          <Group position="center" mb="xl">
            <Badge size="lg" color="cyan">Effective Date: 06-02-2026</Badge>
            <Badge size="lg" variant="outline" color="gray">CDP3D</Badge>
          </Group>

          <Text className={classes.content}>
            This Delivery Policy outlines how orders are processed, shipped, and delivered once placed on our website.
          </Text>

          {/* Order Processing & Dispatch */}
          <Title className={classes.sectionTitle}>
            <ThemeIcon color="cyan" variant="light" radius="xl"><IconTruck size="1rem" /></ThemeIcon>
            Order Processing & Dispatch
          </Title>
          <List spacing="xs" size="sm" center className={classes.list}>
            <List.Item>All orders are processed and prepared for dispatch within <strong>1–4 business days</strong> after successful order confirmation.</List.Item>
            <List.Item>Processing times may vary depending on product type, order volume, or custom 3D printing requirements.</List.Item>
            <List.Item>Business days exclude Sundays and public holidays.</List.Item>
            <List.Item>Once dispatched, the order is handed over to our shipping partners for delivery.</List.Item>
          </List>

          {/* Shipping Partners */}
          <Title className={classes.sectionTitle}>Shipping Partners</Title>
          <Text className={classes.content}>
            We ship orders using <strong>Swift</strong>, which works with multiple reputed logistics partners such as <strong>Delhivery, XpressBees</strong>, and other courier services to ensure reliable and efficient delivery across India.
          </Text>

          {/* Delivery Coverage */}
          <Title className={classes.sectionTitle}>
            <ThemeIcon color="grape" variant="light" radius="xl"><IconMapPin size="1rem" /></ThemeIcon>
            Delivery Coverage
          </Title>
          <Text className={classes.content}>
            We currently deliver <strong>Pan-India</strong>. Delivery availability may depend on courier serviceability for certain pin codes.
          </Text>

          {/* Estimated Delivery Time */}
          <Title className={classes.sectionTitle}>
            <ThemeIcon color="blue" variant="light" radius="xl"><IconCalendarTime size="1rem" /></ThemeIcon>
            Estimated Delivery Time
          </Title>
          <Text className={classes.content}>
            After dispatch, orders are typically delivered within <strong>5–7 business days</strong>, depending on the delivery location and courier partner operations.
          </Text>
          <Text className={classes.content} mt="xs" color="dimmed" size="sm">
            Delivery timelines are estimates and may vary due to factors beyond our control such as weather conditions, regional restrictions, or logistical delays.
          </Text>

          {/* Shipping Charges */}
          <Title className={classes.sectionTitle}>
            <ThemeIcon color="green" variant="light" radius="xl"><IconCurrencyRupee size="1rem" /></ThemeIcon>
            Shipping Charges
          </Title>
          <List spacing="xs" size="sm" center className={classes.list}>
            <List.Item>Shipping charges are calculated based on the weight of the order and will be displayed at checkout before payment.</List.Item>
            <List.Item><strong>Free shipping</strong> is available on orders with a total value above <strong>₹1499</strong>.</List.Item>
          </List>

          {/* Order Tracking */}
          <Title className={classes.sectionTitle}>
            <ThemeIcon color="yellow" variant="light" radius="xl"><IconEye size="1rem" /></ThemeIcon>
            Order Tracking
          </Title>
          <Text className={classes.content}>
            Once your order is shipped, tracking details will be shared via a tracking link, allowing you to monitor the shipment status until delivery.
          </Text>

          {/* Address Accuracy */}
          <Title className={classes.sectionTitle}>Address Accuracy</Title>
          <Text className={classes.content}>
            Customers are responsible for providing complete and accurate delivery details at the time of placing the order. We are not liable for delays or failed deliveries resulting from incorrect or incomplete address information.
          </Text>

          {/* Delays & Exceptions */}
          <Title className={classes.sectionTitle}>
            <ThemeIcon color="orange" variant="light" radius="xl"><IconAlertTriangle size="1rem" /></ThemeIcon>
            Delays & Exceptions
          </Title>
          <Text className={classes.content}>
            While we strive to meet estimated delivery timelines, delays may occur due to unforeseen circumstances. In such cases, delivery will be completed at the earliest possible time.
          </Text>

          <Title className={classes.sectionTitle}>Contact</Title>
          <Text className={classes.content}>
            For more information regarding deliveries, please <Anchor href="/contact-us" color="cyan">contact CDP3D</Anchor>.
          </Text>

        </Card>
      </Container>
    </div>
    );
}
